import { ComponentProps } from "react";
import imageUrlBuilder from "@sanity/image-url";
import clsx from "clsx";
import { SanityImage } from "sanity-image";
import { SanityImageAsset } from "~/sanity/types";
import * as styles from "./Image.css";
import { LoaderFunctionArgs } from "@remix-run/cloudflare";
import { useLoaderData, useRouteLoaderData } from "@remix-run/react";
import { loader } from "~/root";

export type IImage = {
  _type: string;
  asset?: SanityImageAsset;
};

export type ImageProps = Omit<
  ComponentProps<typeof SanityImage>,
  "baseUrl" | "dataset" | "projectId"
> & {
  image: IImage | string;
  width?: number;
  full?: boolean;
  className?: string;
};

const sanityDataset = "production";
const sanityProjectID = "iks90wf7";
// const baseUrl = `https://cdn.sanity.io/images/${sanityProjectID}/${sanityDataset}/`;

// used for SEO images
export const urlFor = ({
  source,
  sanityDataset = "production",
  sanityProjectID = "iks90wf7",
}: {
  source: any;
  sanityProjectID?: string;
  sanityDataset?: string;
}) => {
  // console.log(sanityDataset, sanityProjectID);

  const urlBuilder = imageUrlBuilder({
    projectId: sanityProjectID,
    dataset: sanityDataset,
  });

  return urlBuilder.image(source);
};

export const Image = ({
  image,
  width = 1920,
  full = false,
  className,
  ...props
}: ImageProps) => {
  const data = useRouteLoaderData<typeof loader>("root");

  const sanityDataset = data?.sanityDataset || "production";
  const sanityProjectID = data?.sanityProjectID || "iks90wf7";

  const baseUrl = `https://cdn.sanity.io/images/${sanityProjectID}/${sanityDataset}/`;

  if (typeof image === "string") {
    // If image is a URL string, render a standard <img> tag
    return (
      <img
        draggable={false}
        src={image}
        width={width}
        className={clsx(className, styles.image)}
        sizes={`(min-width: 768px) ${width}px, ${
          full ? "100vw" : `${width}px`
        }`}
        {...props}
      />
    );
  }

  // If image is a Sanity image object, render it using SanityImage
  return image.asset && image.asset._id ? (
    <SanityImage
      draggable={false}
      id={image.asset._id}
      baseUrl={baseUrl}
      className={clsx(className, styles.image)}
      width={width}
      preview={
        props.loading === "eager" ? undefined : image.asset.metadata?.lqip
      }
      sizes={`(min-width: 768px) ${width}px, ${full ? "100vw" : `${width}px`}`}
      {...props}
    />
  ) : null;
};

// export const Image = ({
//   src,
//   image,
//   className,
//   width = 1920,
//   height = 1080,
//   ...props
// }: ImageProps) => {
//   return (
//     <img
//     {...props}
//       width={"100%"}
//       height={"100%"}
//       src={urlFor(image).width(width).auto("format").url()}
//       className={clsx(className)}
//     />
//   );
// };
